import { IConfiguracao } from "@/interfaces/configuracao.interface";
import { GrupoFormSchema } from "@/interfaces/produto.interface";
import { invalidateContextoConfiguracao } from "@/lib/invalidate-contexto-evento";
import { configuracoesAPI } from "@/services/configuracoes.service";
import {
  PayloadAction,
  createListenerMiddleware,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { z } from "zod";
import type { RootState } from "../store";
import { resetState, clearAuthStateOnly } from "./auth.slice";

/** Evita ciclos configuracoes ↔ eventos / configuracoes → general. */
const GENERAL_SET_LOGIN_ID = "generalSlice/setLoginId";
const EVENTOS_SET_EVENTOS = "eventos/SetEventos";

export interface ConfiguracoesState {
  Configuracoes: Array<IConfiguracao>;
  ConfiguracaoId: number | null;
  Grupos: Array<z.infer<typeof GrupoFormSchema>>;
}

const initialState: ConfiguracoesState = {
  Configuracoes: [],
  Grupos: [],
  ConfiguracaoId: localStorage.getItem("ConfiguracaoId") as unknown as number,
};

export const configuracoesSlice = createSlice({
  name: "configuracoes",
  initialState,
  reducers: {
    SetConfiguracaoId(
      state: ConfiguracoesState,
      { payload }: PayloadAction<number | null>
    ) {
      localStorage.setItem("ConfiguracaoId", payload?.toString() || "");
      state.ConfiguracaoId = payload;
    },

    SetConfiguracoes(
      state: ConfiguracoesState,
      { payload }: PayloadAction<Array<IConfiguracao>>
    ) {
      state.Configuracoes = payload;
      state.ConfiguracaoId =
        state.Configuracoes.length == 0 ||
        !state.Configuracoes.some((ev) => ev.Id == state.ConfiguracaoId)
          ? payload[0]?.Id
          : state.ConfiguracaoId;
    },
    SetGrupos(
      state: ConfiguracoesState,
      { payload }: PayloadAction<ConfiguracoesState>
    ) {
      state.Grupos = payload.Grupos;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetState, (state) => {
        state.Configuracoes = [];
        state.ConfiguracaoId = null;
        state.Grupos = [];
        localStorage.setItem("ConfiguracaoId", "");
      })
      .addCase(clearAuthStateOnly, (state) => {
        state.Configuracoes = [];
        state.ConfiguracaoId = null;
        state.Grupos = [];
        localStorage.setItem("ConfiguracaoId", "");
      })
      .addMatcher(
        (action) => action.type === EVENTOS_SET_EVENTOS,
        (state) => {
          state.Configuracoes = null;
        },
      )
      .addMatcher(
        (action) => action.type === GENERAL_SET_LOGIN_ID,
        (state) => {
          state.ConfiguracaoId = null;
          state.Configuracoes = null;
        },
      );
  },
});

export const configuracoesReducer = configuracoesSlice.reducer;
export const { SetConfiguracaoId, SetConfiguracoes, SetGrupos } =
  configuracoesSlice.actions;

const getConfigsSelector = createSelector(
  [(state: RootState) => state.configuracoesReducer],
  (state: ConfiguracoesState) => state.Configuracoes
);

const SelectedConfigSelector = createSelector(
  [(state: RootState) => state.configuracoesReducer],
  (state: ConfiguracoesState) =>
    state.Configuracoes?.find((ev) => ev.Id == state.ConfiguracaoId)
);
const GruposEventoSelector = createSelector(
  [(state: RootState) => state.configuracoesReducer],
  (state: ConfiguracoesState) => state.Grupos
);

export const configuracoesListenerMiddleware = createListenerMiddleware();
configuracoesListenerMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    if (action.type == configuracoesSlice.actions.SetConfiguracoes.type) {
      if (
        (previousState as RootState).configuracoesReducer.Configuracoes
          ?.length == 0
      ) {
        return true;
      }
    }

    if (action.type == configuracoesSlice.actions.SetConfiguracaoId.type) {
      return true;
    }
  },
  effect: async (action, listenerApi) => {
    const { ConfiguracaoId } = (listenerApi.getState() as RootState)
      .configuracoesReducer;
    listenerApi.cancelActiveListeners();

    if (action.type === configuracoesSlice.actions.SetConfiguracaoId.type) {
      const previousConfiguracaoId = (
        listenerApi.getOriginalState() as RootState
      ).configuracoesReducer.ConfiguracaoId;
      if (Number(previousConfiguracaoId) !== Number(ConfiguracaoId)) {
        invalidateContextoConfiguracao(listenerApi.dispatch);
      }
    }

    if (ConfiguracaoId) {
      listenerApi.dispatch(
        configuracoesAPI.endpoints.getGrupos.initiate(
          ConfiguracaoId as number,
          {
            forceRefetch: true,
          }
        )
      );
    }
  },
});

export const configuracoesSelectors = {
  getConfigsSelector,
  SelectedConfigSelector,
  GruposEventoSelector,
};
